/**
 * accountService — Profile editing, image upload, handle management,
 * notification settings, account deletion.
 *
 * Profile + notification settings use direct Firestore reads/writes
 * (same pattern as the mobile app — no Cloud Function needed).
 * Handle ops + image moderation + account deletion use Cloud Functions.
 */

import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { FIRESTORE_DB, FIREBASE_STORAGE, FIREBASE_FUNCTIONS, FIREBASE_AUTH } from '../firebaseConfig';
import type {
  EditableProfile,
  NotificationSettings,
  DEFAULT_NOTIFICATION_SETTINGS,
  ImageValidationResult,
  HandleValidationResult,
  HandleAvailabilityResult,
  HandleRestrictionResult,
} from '../types/account';

// ─── Cloud Function refs ────────────────────────────────────────────────────

const profileImageValidatorFn = httpsCallable(FIREBASE_FUNCTIONS, 'profileImageValidatorEurW');
const validateHandleFn = httpsCallable(FIREBASE_FUNCTIONS, 'validateHandleEurW');
const checkHandleAvailabilityFn = httpsCallable(FIREBASE_FUNCTIONS, 'checkTetherHandleAvailabilityEurW');
const updateHandleFn = httpsCallable(FIREBASE_FUNCTIONS, 'updateTetherHandleEurW');
const checkHandleRestrictionFn = httpsCallable(FIREBASE_FUNCTIONS, 'checkHandleUpdateRestrictionEurW');
const deleteUserFn = httpsCallable(FIREBASE_FUNCTIONS, 'deleteUserEurW');
const onboardingStartersFn = httpsCallable(FIREBASE_FUNCTIONS, 'onboardingConversationStartersEurW');

// ─── Profile CRUD (direct Firestore) ────────────────────────────────────────

/** Read the current user's full profile from Firestore */
export async function getMyProfile(uid: string): Promise<Partial<EditableProfile>> {
  const snap = await getDoc(doc(FIRESTORE_DB, 'users', uid));
  if (!snap.exists()) return {};
  return snap.data() as Partial<EditableProfile>;
}

/**
 * Save profile fields to Firestore (merge).
 * Same pattern as the mobile app — direct `updateDoc`, no Cloud Function.
 */
export async function saveProfile(uid: string, fields: Partial<EditableProfile>): Promise<void> {
  await updateDoc(doc(FIRESTORE_DB, 'users', uid), {
    ...fields,
    profileSet: true,
    profileComplete: true,
  });
}

// ─── Profile Image ──────────────────────────────────────────────────────────

/**
 * Upload a profile image to Firebase Storage, moderate it, then save the URL.
 * Storage path: profileImages/{userId} (single file, overwritten).
 */
export async function uploadProfileImage(
  uid: string,
  file: File,
): Promise<{ url: string; isValid: boolean; reason: string }> {
  // 1. Validate size (5 MB max)
  if (file.size > 5 * 1024 * 1024) {
    throw new Error('Image must be under 5 MB');
  }

  // 2. Upload to Storage
  const storageRef = ref(FIREBASE_STORAGE, `profileImages/${uid}`);
  await uploadBytes(storageRef, file, { contentType: file.type });
  const downloadUrl = await getDownloadURL(storageRef);

  // 3. Run AI moderation
  const result = await profileImageValidatorFn({ imageUrl: downloadUrl, imageType: 'profile' });
  const validation = result.data as ImageValidationResult;

  if (!validation.isValid) {
    // Image rejected — clean up would be ideal but the CF may already handle it
    return { url: downloadUrl, isValid: false, reason: validation.reason };
  }

  // 4. Save URL to user doc
  await updateDoc(doc(FIRESTORE_DB, 'users', uid), { profileImageUrl: downloadUrl });

  return { url: downloadUrl, isValid: true, reason: '' };
}

// ─── Handle Management ──────────────────────────────────────────────────────

export async function validateHandle(handle: string): Promise<HandleValidationResult> {
  const result = await validateHandleFn({ handle });
  return result.data as HandleValidationResult;
}

export async function checkHandleAvailability(handle: string): Promise<HandleAvailabilityResult> {
  const result = await checkHandleAvailabilityFn({ handle });
  return result.data as HandleAvailabilityResult;
}

export async function updateHandle(newHandle: string): Promise<{ success: boolean; message: string }> {
  const result = await updateHandleFn({ newHandle });
  return result.data as { success: boolean; message: string };
}

export async function checkHandleRestriction(): Promise<HandleRestrictionResult> {
  const result = await checkHandleRestrictionFn({});
  return result.data as HandleRestrictionResult;
}

// ─── Notification Settings (direct Firestore) ──────────────────────────────

const NOTIF_PATH = (uid: string) =>
  doc(FIRESTORE_DB, 'users', uid, 'notificationSettings', 'settings');

export async function getNotificationSettings(uid: string): Promise<NotificationSettings | null> {
  const snap = await getDoc(NOTIF_PATH(uid));
  if (!snap.exists()) return null;
  return snap.data() as NotificationSettings;
}

export async function saveNotificationSettings(
  uid: string,
  settings: Partial<NotificationSettings>,
): Promise<void> {
  await setDoc(NOTIF_PATH(uid), settings, { merge: true });
}

// ─── Privacy (direct Firestore, root user doc) ──────────────────────────────

/**
 * PPL-169 — whether strangers can send this user break-the-ice room invites.
 * Lives on the ROOT user doc (not notificationSettings): the server gate in
 * createMediationInviteEurW and the match pool both read it per-user without
 * a subcollection lookup. Absent = allowed, so only a literal `false` opts out.
 */
export async function getAllowBreakTheIceInvites(uid: string): Promise<boolean> {
  const snap = await getDoc(doc(FIRESTORE_DB, 'users', uid));
  return snap.data()?.allowBreakTheIceInvites !== false;
}

export async function setAllowBreakTheIceInvites(uid: string, allowed: boolean): Promise<void> {
  await setDoc(doc(FIRESTORE_DB, 'users', uid), { allowBreakTheIceInvites: allowed }, { merge: true });
}

// ─── Account Deletion ───────────────────────────────────────────────────────

/**
 * Full account deletion flow:
 * 1. Call deleteUserEurW Cloud Function (deletes Firestore data)
 * 2. Delete Firebase Auth account (client-side)
 */
export async function deleteAccount(): Promise<void> {
  // Step 1: Delete Firestore data via Cloud Function
  const result = await deleteUserFn({});
  const data = result.data as { text: string };
  if (data.text !== 'Success') {
    throw new Error(data.text || 'Account deletion failed');
  }

  // Step 2: Delete Firebase Auth account
  const currentUser = FIREBASE_AUTH.currentUser;
  if (currentUser) {
    await currentUser.delete();
  }
}

// ─── Onboarding ─────────────────────────────────────────────────────────────

/**
 * Save onboarding data to both locations (matching mobile pattern):
 * - users/{uid} (top-level fields)
 * - users/{uid}/preferences/userProfile (subcollection)
 */
export async function saveOnboardingStep(
  uid: string,
  data: Record<string, any>,
): Promise<void> {
  // Write to user root
  await updateDoc(doc(FIRESTORE_DB, 'users', uid), data);

  // Write to preferences subcollection
  await setDoc(
    doc(FIRESTORE_DB, 'users', uid, 'preferences', 'userProfile'),
    { ...data, timestamp: new Date() },
    { merge: true },
  );
}

/**
 * Mark onboarding as complete.
 * Writes to users/{uid}/meta/firstLoad and fires conversation starters.
 */
export async function completeOnboarding(uid: string): Promise<void> {
  await setDoc(doc(FIRESTORE_DB, 'users', uid, 'meta', 'firstLoad'), {
    timestamp: new Date(),
    device: 'web',
    completed: true,
  });

  fireOnboardingStarters('completed');
}

/**
 * Kick off the onboarding conversation starters. Deliberately NOT awaited by
 * callers so it never blocks the post-onboarding navigation. Invoked exactly
 * ONCE — the callable is non-idempotent (writes starter messages + a
 * notification and consumes a token), so a retry could double-charge/duplicate
 * when the first call succeeds server-side but the response is lost. Unlike the
 * old `.catch(() => {})`, a failure is logged rather than silently discarded;
 * the client-mirror `onboarding_finished` covers the funnel when it fails
 * (PostHog plan §5.3).
 *
 * The `outcome` payload is the trusted signal the server reads for its own
 * `onboarding_finished` event.
 */
function fireOnboardingStarters(outcome: 'completed' | 'skipped'): void {
  onboardingStartersFn({ outcome, device: 'web' }).catch((err) =>
    console.warn('onboardingConversationStartersEurW failed', err),
  );
}

/** Check if onboarding was already completed */
export async function getOnboardingStatus(uid: string): Promise<boolean> {
  const snap = await getDoc(doc(FIRESTORE_DB, 'users', uid, 'meta', 'firstLoad'));
  if (!snap.exists()) return false;
  const data = snap.data();
  return data?.completed === true || data?.completed === 'skipped';
}

/** Mark onboarding as skipped */
export async function skipOnboarding(uid: string): Promise<void> {
  await setDoc(doc(FIRESTORE_DB, 'users', uid, 'meta', 'firstLoad'), {
    timestamp: new Date(),
    device: 'web',
    completed: 'skipped',
  });
  fireOnboardingStarters('skipped');
}
